/**
 * Evidence list — supporting/contradicting statements for decisions,
 * transitions, scores, metrics, and rules. Full CRUD resource.
 * Columns: title, evidence_type, confidence_score, artifact_id, created_at.
 */
import { useMemo } from "react";
import { useNavigation } from "@refinedev/core";
import type { ColumnDef } from "@tanstack/react-table";
import type { Evidence } from "@aos/shared";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { PageHeader } from "@/components/layout/PageHeader";
import { RefLink } from "@/components/inspector";
import { RunsDataTable } from "../_shared/RunsDataTable";
import { formatDateTime, orDash } from "../_shared/runs-ui";
import { PolarityBadge } from "./_polarity";

function confidenceLabel(value: number | null): string {
  if (value === null || value === undefined) return "—";
  return `${Math.round(value * 100)}%`;
}

/** Human claim-target label (the claim *type*; the raw id is never shown). */
function claimTargetLabel(record: Evidence): string {
  if (!record.claim_type) return "—";
  return record.claim_type;
}

export function EvidenceList() {
  const { show, create } = useNavigation();

  const columns = useMemo<ColumnDef<Evidence>[]>(
    () => [
      {
        id: "title",
        accessorKey: "title",
        header: "Title",
        cell: ({ row }) => (
          <span className="font-medium">{row.original.title}</span>
        ),
      },
      {
        id: "evidence_type",
        accessorKey: "evidence_type",
        header: "Type",
        cell: ({ row }) => orDash(row.original.evidence_type),
      },
      {
        id: "polarity",
        accessorKey: "polarity",
        header: "Polarity",
        cell: ({ row }) => <PolarityBadge polarity={row.original.polarity} />,
      },
      {
        id: "claim_target",
        header: "Claim target",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="capitalize">{claimTargetLabel(row.original)}</span>
        ),
      },
      {
        id: "confidence_score",
        accessorKey: "confidence_score",
        header: "Confidence",
        cell: ({ row }) => confidenceLabel(row.original.confidence_score),
      },
      {
        id: "artifact_id",
        accessorKey: "artifact_id",
        header: "Artifact",
        enableSorting: false,
        cell: ({ row }) => (
          <span onClick={(e) => e.stopPropagation()}>
            <RefLink resource="artifacts" id={row.original.artifact_id} />
          </span>
        ),
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {formatDateTime(row.original.created_at)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Evidence"
        subtitle="Statements that support or contradict decisions, scores, and rules."
        icon="evidence"
        actions={
          <Button size="sm" onClick={() => create("evidence")}>
            <Icon name="create" size={16} className="mr-2" aria-hidden /> New
            evidence
          </Button>
        }
      />
      <RunsDataTable<Evidence>
        resource="evidence"
        columns={columns}
        onRowClick={(record) => show("evidence", record.id)}
        emptyMessage="No evidence recorded yet."
      />
    </div>
  );
}

export default EvidenceList;
